import React, { useCallback, useEffect, useRef, useState } from 'react';
import { type InputProps, InputWithoutArrows } from './ui/Input/Input';

const getDecimalPlaces = (step: number): number => {
  if (step >= 1) return 0;
  const stepStr = step.toString();
  const decimalIndex = stepStr.indexOf('.');
  return decimalIndex === -1 ? 0 : stepStr.length - decimalIndex - 1;
};

export type NumericInputProps = InputProps & {
  overrideValue: number | null;
  defaultValue: number | null;
  onUpdate: (newValue: number | null) => void;
  max?: number;
  min?: number;
  step?: number;
};

export const useUpdatingValue = (
  overrideValue: number | null,
  defaultValue: number | null,
  onUpdate: (currentValue: number | null) => void,
  max: number,
  min: number,
  step: number,
): Partial<InputProps> => {
  const [updatedOverrideValue, _setUpdatedOverrideValue] = useState<
    number | null
  >(null);

  const hasChanged = useRef<boolean>(false);

  const setUpdatedOverrideValue = useCallback(
    (newValue: number | null) => {
      hasChanged.current = true;
      _setUpdatedOverrideValue(
        newValue === null ? null : Math.min(Math.max(newValue, min), max),
      );
    },
    [_setUpdatedOverrideValue, max, min],
  );

  useEffect(() => {
    _setUpdatedOverrideValue(overrideValue);
  }, [overrideValue, defaultValue]);

  const submit = useCallback(() => {
    if (hasChanged.current) {
      onUpdate(updatedOverrideValue);
      hasChanged.current = false;
    }
  }, [onUpdate, updatedOverrideValue]);

  return {
    value:
      updatedOverrideValue !== null
        ? Number(updatedOverrideValue.toFixed(getDecimalPlaces(step)))
        : '',
    onKeyPress: (e) => e.key === 'Enter' && submit(),
    onKeyDown: (e) => {
      const currentValue =
        e.currentTarget.value === ''
          ? Number((defaultValue || min).toFixed(getDecimalPlaces(step)))
          : Number(e.currentTarget.value);
      if (e.key === 'ArrowUp') {
        setUpdatedOverrideValue(currentValue + step);
        e.preventDefault();
      } else if (e.key === 'ArrowDown') {
        setUpdatedOverrideValue(currentValue - step);
        e.preventDefault();
      }
    },
    onChange: (e) => {
      const newValue = e.target.value !== '' ? Number(e.target.value) : null;
      setUpdatedOverrideValue(newValue);
    },
    onBlur: submit,
  };
};

export const NumericInput = React.memo(
  ({
    overrideValue,
    defaultValue,
    onUpdate,
    max = Infinity,
    min = -Infinity,
    step = 0.1,
    ...restProps
  }: NumericInputProps): JSX.Element => {
    const inputProps = useUpdatingValue(
      overrideValue,
      defaultValue,
      onUpdate,
      max,
      min,
      step,
    );

    return (
      <InputWithoutArrows
        {...restProps}
        type="number"
        placeholder={
          defaultValue !== null
            ? Number(defaultValue.toFixed(getDecimalPlaces(step))).toString()
            : ''
        }
        {...inputProps}
        style={{
          width: 60,
          ...inputProps.style,
        }}
        step={step}
      />
    );
  },
);
